import React, { useState, useCallback, useMemo } from 'react'

import { bool } from 'prop-types'

import useJoinProgram from 'Pods/MySubscriptions/Hooks/useJoinProgram'
import CustomSelect from 'Pods/Shared/CustomSelect/CustomSelect'
import I18n from 'Pods/Shared/I18n'
import JoinProgramModal from 'Pods/Shared/JoinProgramModal/JoinProgramModal'

import useToggle from 'Utils/useToggle'

import EarningsSection from './EarningsSection'
import EarningsStatsGroup from './EarningsStatsGroup'
import EssentialsBanner from './EssentialsBanner'
import MonthlySummarySection from './MonthlySummarySection'
import PayoutRateBanner from './PayoutRateBanner'
import RegularEarningsDetailPanel from './RegularEarningsDetailPanel'
import SubscriptionDetailPanel from './SubscriptionDetailPanel'
import useOverviewKPI from '../../../Hooks/useOverviewKPI'
import { PERIOD_OPTIONS } from '../../../Static/constants'

function RedesignedOverviewTab({ isSubscriptionParticipant = false, payoutRateArticleLink = false }) {
  const [isParticipating, setIsParticipating] = useState(isSubscriptionParticipant)
  const [presetMonth, setPresetMonth] = useState(null)
  const subscriptionDetail = useToggle()
  const regularDetail = useToggle()

  const { open: openRegularDetail, close: closeRegularDetail } = regularDetail

  const handleViewMonthDetails = useCallback((yearMonth) => {
    setPresetMonth(yearMonth || null)
    openRegularDetail()
  }, [openRegularDetail])

  const handleViewRegularDetails = useCallback(() => {
    setPresetMonth(null)
    openRegularDetail()
  }, [openRegularDetail])

  const handleRegularDetailClose = useCallback(() => {
    closeRegularDetail()
    setPresetMonth(null)
  }, [closeRegularDetail])

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', ...options }),
    []
  )

  const messages = useMemo(() => ({
    success: translate('my_subscriptions.join_modal.success'),
    error: translate('my_subscriptions.join_modal.error'),
  }), [translate])

  const {
    isModalOpen,
    isJoining,
    handleOpenModal,
    handleCloseModal,
    handleJoinProgram,
  } = useJoinProgram({
    messages,
    onSuccess: () => setIsParticipating(true),
    keepOpenOnSuccess: true,
  })

  const {
    tiles,
    payoutRate,
    subscriptionChartData,
    subscriptionDownloadCount,
    earningsOverview: { data, earningsScope, isDataFetched, onSortChange },
  } = useOverviewKPI()

  return (
    <div className="overview-redesigned">
      <EssentialsBanner isParticipating={isParticipating} onHowItWorks={handleOpenModal} />

      <div className="overview-earnings-container">
        <div className="overview-earnings-container__header">
          <h2 className="cgt-heading-h4">Earnings</h2>
          <CustomSelect
            className="overview-select"
            valueClassName="cgt-body-base-semibold"
            options={PERIOD_OPTIONS}
            onChange={onSortChange}
            value={PERIOD_OPTIONS.find((opt) => opt.value === earningsScope) || PERIOD_OPTIONS[3]}
            ariaLabel="Earnings time range"
          />
        </div>

        <EarningsStatsGroup
          tiles={tiles}
          isSubscriptionParticipant={isParticipating}
          onEnableSubscription={handleOpenModal}
          onViewSubscriptionDetails={subscriptionDetail.open}
          onViewRegularDetails={handleViewRegularDetails}
        />

        <EarningsSection
          isDataFetched={isDataFetched}
          data={data}
          subscriptionChartData={subscriptionChartData}
        />

        <PayoutRateBanner payoutRate={payoutRate} showArticleLink={payoutRateArticleLink} />
      </div>

      <MonthlySummarySection onViewDetails={handleViewMonthDetails} />

      {subscriptionDetail.isOpen && (
        <SubscriptionDetailPanel
          isOpen={subscriptionDetail.isOpen}
          onClose={subscriptionDetail.close}
          isParticipating={isParticipating}
          onEnableSubscription={handleOpenModal}
          thisMonthAmount={tiles?.subscriptionThisMonth || 0}
          downloadCount={subscriptionDownloadCount}
        />
      )}

      {regularDetail.isOpen && (
        <RegularEarningsDetailPanel
          isOpen={regularDetail.isOpen}
          onClose={handleRegularDetailClose}
          thisMonthAmount={tiles?.regularThisMonth || 0}
          initialMonth={presetMonth}
        />
      )}

      <JoinProgramModal
        isOpen={isModalOpen}
        isJoining={isJoining}
        mode={isParticipating ? 'info' : 'join'}
        redesigned
        onClose={handleCloseModal}
        onConfirm={handleJoinProgram}
        translate={translate}
      />
    </div>
  )
}

RedesignedOverviewTab.propTypes = {
  isSubscriptionParticipant: bool,
  payoutRateArticleLink: bool,
}

export default RedesignedOverviewTab
